/**
 * Team Peer Evaluations Card
 *
 * Card component for team dashboard showing recent peer evaluations overview.
 * Similar to TeamEvaluationsCard but for 360-degree peer feedback.
 * Includes "+ Nieuw" button that opens employee selector then PeerEvaluationCreateModal.
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { useTeamMembers } from './useTeam';
import { peerEvaluationKeys, type PeerEvaluation, PeerEvaluationCreateModal } from '../peer-evaluations';
import { peerEvaluationApi } from '../../lib/peerEvaluationApi';

const STATUS_CONFIG = {
  concept: { label: 'Concept', color: 'bg-yellow-100 text-yellow-800' },
  ingediend: { label: 'Ingediend', color: 'bg-green-100 text-green-800' },
} as const;

type PeerEvaluationStatus = keyof typeof STATUS_CONFIG;

export function TeamPeerEvaluationsCard() {
  const { data: teamMembers, isLoading: teamLoading } = useTeamMembers();

  // Modal state
  const [showEmployeeSelect, setShowEmployeeSelect] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<{ id: number; name: string } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');

  // Get all team member IDs
  const teamMemberIds = useMemo(() => {
    return teamMembers?.map((m) => m.employeeId) || [];
  }, [teamMembers]);

  // Use useQueries to fetch peer evaluations for all team members in parallel
  const peerEvalQueries = useQueries({
    queries: teamMemberIds.map((employeeId) => ({
      queryKey: peerEvaluationKeys.list(employeeId),
      queryFn: () => peerEvaluationApi.getByEmployeeId(employeeId),
      enabled: employeeId > 0,
    })),
  });

  const isLoading = teamLoading || peerEvalQueries.some((q) => q.isLoading);

  // Combine all peer evaluations from all team members
  const allPeerEvaluations = useMemo(() => {
    const evals: PeerEvaluation[] = [];
    peerEvalQueries.forEach((q) => {
      if (q.data?.data) {
        evals.push(...q.data.data);
      }
    });
    // Remove duplicates by ID (in case same evaluation appears for multiple employees)
    const uniqueMap = new Map<number, PeerEvaluation>();
    evals.forEach((e) => uniqueMap.set(e.id, e));
    return Array.from(uniqueMap.values());
  }, [peerEvalQueries]);

  // Count by status
  const statusCounts = useMemo(() => ({
    concept: allPeerEvaluations.filter((e) => e.status === 'concept').length,
    ingediend: allPeerEvaluations.filter((e) => e.status === 'ingediend').length,
  }), [allPeerEvaluations]);

  // Get recent peer evaluations (max 5)
  const recentPeerEvaluations = useMemo(() => {
    return [...allPeerEvaluations]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  }, [allPeerEvaluations]);

  // Filter team members for employee selector
  const filteredTeamMembers = useMemo(() => {
    if (!teamMembers) return [];
    if (!employeeSearchQuery.trim()) return teamMembers;
    const query = employeeSearchQuery.toLowerCase();
    return teamMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(query) ||
        member.email.toLowerCase().includes(query)
    );
  }, [teamMembers, employeeSearchQuery]);

  // Handlers
  const handleCreateNew = () => {
    setEmployeeSearchQuery('');
    setShowEmployeeSelect(true);
  };

  const handleEmployeeSelect = (employeeId: number, employeeName: string) => {
    setSelectedEmployee({ id: employeeId, name: employeeName });
    setShowEmployeeSelect(false);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setSelectedEmployee(null);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setSelectedEmployee(null);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Peer Evaluaties</h3>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Team Peer Evaluaties</h3>
          <button
            onClick={handleCreateNew}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            title="Nieuwe peer evaluatie aanmaken"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuw
          </button>
        </div>

        {/* Status badges */}
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {statusCounts.concept} concept
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {statusCounts.ingediend} ingediend
          </span>
        </div>

        {recentPeerEvaluations.length > 0 ? (
          <div className="space-y-3">
            {recentPeerEvaluations.map((peerEval) => (
              <Link
                key={peerEval.id}
                to={`/peer-evaluatie/${peerEval.id}`}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {peerEval.employeeName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    door {peerEval.evaluatorName}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[peerEval.status as PeerEvaluationStatus]?.color || 'bg-gray-100'}`}>
                      {STATUS_CONFIG[peerEval.status as PeerEvaluationStatus]?.label || peerEval.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(peerEval.createdAt).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}

            <Link
              to="/team/peer-feedback"
              className="block text-center text-sm text-blue-600 hover:text-blue-800 py-2"
            >
              Bekijk alle {allPeerEvaluations.length} peer evaluaties →
            </Link>
          </div>
        ) : (
          <div className="text-center py-6">
            <svg
              className="w-12 h-12 text-gray-300 mx-auto mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="text-gray-500 text-sm">
              Nog geen peer evaluaties voor je team.
            </p>
            <Link
              to="/team/peer-feedback"
              className="inline-block mt-2 text-sm text-blue-600 hover:text-blue-800"
            >
              Bekijk overzicht →
            </Link>
          </div>
        )}
      </div>

      {/* Employee Selection Modal */}
      {showEmployeeSelect && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Selecteer Medewerker</h3>
            <p className="text-sm text-gray-600 mb-4">
              Kies de medewerker die beoordeeld moet worden.
            </p>

            {/* Search filter */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Zoek op naam of email..."
                value={employeeSearchQuery}
                onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredTeamMembers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Geen medewerkers gevonden voor "{employeeSearchQuery}"
                </p>
              ) : (
                filteredTeamMembers.map((member) => (
                  <button
                    key={member.id}
                    onClick={() => handleEmployeeSelect(member.employeeId, member.name)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
                  >
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-medium">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{member.name}</p>
                      <p className="text-sm text-gray-500 truncate">{member.email}</p>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowEmployeeSelect(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Peer Evaluation Create Modal */}
      {showCreateModal && selectedEmployee && (
        <PeerEvaluationCreateModal
          isOpen={showCreateModal}
          onClose={handleCloseCreateModal}
          employeeId={selectedEmployee.id}
          employeeName={selectedEmployee.name}
          onSuccess={handleCreateSuccess}
        />
      )}
    </>
  );
}
